import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ticketsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import ws from '../services/wsService';
import { getTicketViewMode, setTicketViewMode, ticketHref, TicketViewMode } from '../utils/ticketView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { PriorityBadge } from '../components/common/PriorityBadge';
import { SLABadge } from '../components/common/SLABadge';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  TicketIcon,
  ClockIcon,
  BellIcon,
  TrashIcon,
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description?: string;
  status: string;
  priority: string;
  isGameInstall?: boolean;
  assignedTo?: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  telegramUserId: string;
  telegramUsername?: string;
  telegramFirstName?: string;
  telegramLastName?: string;
  telegramChatType?: string;
  telegramChatTitle?: string;
  firstResponseTime?: string;
  sla?: {
    firstResponse: {
      target: number;
      actual?: number;
      breached: boolean;
    };
    resolution: {
      target: number;
      actual?: number;
      breached: boolean;
    };
  };
}

const TicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  // Two states: `searchTerm` updates on every keystroke (controls the input),
  // `debouncedSearch` is what we actually pass to the API. Without this the
  // list re-fetches on every keypress and the UI looks like "ничего не
  // происходит" while requests pile up and arrive out of order.
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Initial filters come straight from the URL (dashboard deep links), so the
  // very first fetch is already filtered — otherwise an unfiltered first
  // request could land after the filtered one and overwrite it.
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || '');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [csatFilter, setCsatFilter] = useState('');
  // «Установка/обновление игр» flag filter: '' = все, 'yes' = только эти,
  // 'no' = скрыть эти.
  const [gameInstallFilter, setGameInstallFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const [unreadTicketsCount, setUnreadTicketsCount] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  // Where a row click lands: classic detail page (default) or the new
  // Workspace. Remembered per-browser; backward-compatible "переключалка".
  const [viewMode, setViewMode] = useState<TicketViewMode>(getTicketViewMode());
  const changeViewMode = (mode: TicketViewMode) => {
    setTicketViewMode(mode);
    setViewMode(mode);
  };

  useEffect(() => {
    // Получаем фильтр клиента из URL параметров
    const clientFromUrl = searchParams.get('client');
    if (clientFromUrl && clientFromUrl !== clientFilter) {
      setClientFilter(clientFromUrl);
    }
    // Deep links from the dashboard tiles: /tickets?status=new
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl !== null && statusFromUrl !== statusFilter) setStatusFilter(statusFromUrl);
  }, [searchParams]);

  // Debounce search: only commit the typed term after 350ms of quiet so we
  // don't fire a /api/tickets/ request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 whenever any filter or search term changes — otherwise
  // a stale `currentPage` can land you on an empty page after a narrowing search.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, priorityFilter, clientFilter, csatFilter, gameInstallFilter]);

  // Ref to fetchTickets so the WS-subscribing useEffect below can call the
  // latest version without forward-referencing it (the function is declared
  // further down the file).
  const fetchRef = useRef<(opts?: { silent?: boolean }) => void>(() => {});

  useEffect(() => {
    fetchRef.current();
  }, [debouncedSearch, statusFilter, priorityFilter, clientFilter, csatFilter, gameInstallFilter, currentPage]);

  // Live refresh on WS events so the list stays in sync without a manual
  // refresh. Debounced ~800ms because a burst of activity (new ticket +
  // immediate first message) would otherwise trigger 2-3 back-to-back
  // refetches in a row.
  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        fetchRef.current({ silent: true });
      }, 800);
    };

    ws.joinDashboard();
    ws.on('new_ticket', scheduleRefetch);
    ws.on('ticket_updated', scheduleRefetch);

    return () => {
      if (pending) clearTimeout(pending);
      ws.off('new_ticket', scheduleRefetch);
      ws.off('ticket_updated', scheduleRefetch);
    };
  }, []);

  // Refs to the *latest* filter values — WS handlers are registered once and
  // close over their initial scope, so we route through refs to always pick
  // up current values without re-subscribing.
  const filtersRef = useRef({
    page: currentPage,
    search: debouncedSearch,
    status: statusFilter,
    priority: priorityFilter,
    client: clientFilter,
    csat: csatFilter,
    gameInstall: gameInstallFilter,
  });
  filtersRef.current = {
    page: currentPage,
    search: debouncedSearch,
    status: statusFilter,
    priority: priorityFilter,
    client: clientFilter,
    csat: csatFilter,
    gameInstall: gameInstallFilter,
  };

  const fetchTickets = useCallback(async (opts: { silent?: boolean } = {}) => {
    const { silent = false } = opts;
    try {
      // Silent refetch (WS-triggered) avoids the loading skeleton flash —
      // the user shouldn't see the list blink every time a new ticket arrives.
      if (!silent) setLoading(true);
      const f = filtersRef.current;
      const params = {
        page: f.page,
        limit: 10,
        search: f.search,
        status: f.status,
        priority: f.priority,
        client: f.client,
        csat: f.csat,
        gameInstall: f.gameInstall,
      };

      const response = await ticketsApi.getAll(params);
      const apiData = response.data?.data || {};
      const fetchedTickets = apiData.tickets || [];
      setTickets(fetchedTickets);
      setTotalPages(apiData.pagination?.pages || 1);

      // Подсчитываем количество новых и непрочитанных заявок
      const newCount = fetchedTickets.filter((ticket: Ticket) => isNewTicket(ticket)).length;
      const unreadCount = fetchedTickets.filter((ticket: Ticket) => isUnreadTicket(ticket)).length;
      setNewTicketsCount(newCount);
      setUnreadTicketsCount(unreadCount);
    } catch (error) {
      // Silent refetches don't toast — a passing WS hiccup shouldn't yell.
      if (!silent) toast.error('Ошибка загрузки заявок');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Keep the ref pointing at the latest fetchTickets so WS-triggered
  // refetches always see current filter/page closures.
  fetchRef.current = fetchTickets;

  // Функция для расчета SLA
  const calculateSLA = (ticket: Ticket) => {
    if (!ticket.sla) return null;

    const now = new Date().getTime();
    const createdAt = new Date(ticket.createdAt).getTime();
    const firstResponseTime = ticket.firstResponseTime ? new Date(ticket.firstResponseTime).getTime() : null;

    // Время до первого ответа
    let firstResponseActual = null;
    let firstResponseBreached = false;
    
    if (firstResponseTime) {
      firstResponseActual = Math.floor((firstResponseTime - createdAt) / 1000);
      firstResponseBreached = firstResponseActual > (ticket.sla.firstResponse.target || 300);
    } else {
      // Если ответа еще нет, проверяем, не нарушен ли SLA
      const timeSinceCreation = Math.floor((now - createdAt) / 1000);
      firstResponseBreached = timeSinceCreation > (ticket.sla.firstResponse.target || 300);
    }

    // Время до разрешения (если заявка решена)
    let resolutionActual = null;
    let resolutionBreached = false;
    
    if (ticket.status === 'resolved' || ticket.status === 'closed') {
      // Для простоты считаем, что заявка решена при изменении статуса
      const resolutionTime = firstResponseTime || createdAt;
      resolutionActual = Math.floor((resolutionTime - createdAt) / 1000);
      resolutionBreached = resolutionActual > (ticket.sla.resolution.target || 3600);
    }

    return {
      firstResponse: {
        target: ticket.sla.firstResponse.target || 300,
        actual: firstResponseActual,
        breached: firstResponseBreached,
        remaining: firstResponseActual ? null : Math.max(0, (ticket.sla.firstResponse.target || 300) - Math.floor((now - createdAt) / 1000))
      },
      resolution: {
        target: ticket.sla.resolution.target || 3600,
        actual: resolutionActual,
        breached: resolutionBreached
      }
    };
  };

  // Функция для проверки, является ли заявка новой (не старше 1 часа)
  const isNewTicket = (ticket: Ticket) => {
    const now = new Date().getTime();
    const createdAt = new Date(ticket.createdAt).getTime();
    const oneHour = 60 * 60 * 1000; // 1 час в миллисекундах
    return (now - createdAt) < oneHour && ticket.status === 'new';
  };

  // Функция для проверки, является ли заявка непрочитанной (не старше 24 часов)
  const isUnreadTicket = (ticket: Ticket) => {
    const now = new Date().getTime();
    const createdAt = new Date(ticket.createdAt).getTime();
    const oneDay = 24 * 60 * 60 * 1000; // 24 часа в миллисекундах
    return (now - createdAt) < oneDay && ticket.status === 'new';
  };

  // Функция для форматирования времени SLA
  const formatSLATime = (seconds: number) => {
    if (seconds < 60) return `${seconds}с`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}м`;
    return `${Math.floor(seconds / 3600)}ч ${Math.floor((seconds % 3600) / 60)}м`;
  };


  // Компактное форматирование даты для таблицы
  const formatDateCompact = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins}м назад`;
    if (diffHours < 24) return `${diffHours}ч назад`;
    if (diffDays < 7) return `${diffDays}д назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  const handleDelete = async (ticketId: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту заявку?')) {
      return;
    }

    try {
      await ticketsApi.delete(ticketId);
      toast.success('Заявка успешно удалена!');
      fetchTickets(); // Обновляем список заявок
    } catch (error) {
      toast.error('Ошибка при удалении заявки');
    }
  };

  // Функция для генерации страниц с "окном"
  const getPageNumbers = (currentPage: number, totalPages: number, windowSize: number = 2) => {
    const pages: (number | string)[] = [];
    
    if (totalPages <= 7) {
      // Если страниц мало, показываем все
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // Всегда показываем первую страницу
    pages.push(1);
    
    // Вычисляем начало и конец окна вокруг текущей страницы
    let start = Math.max(2, currentPage - windowSize);
    let end = Math.min(totalPages - 1, currentPage + windowSize);
    
    // Если текущая страница близко к началу, расширяем окно вправо
    if (currentPage <= windowSize + 1) {
      end = Math.min(totalPages - 1, 2 * windowSize + 2);
    }
    
    // Если текущая страница близко к концу, расширяем окно влево
    if (currentPage >= totalPages - windowSize) {
      start = Math.max(2, totalPages - 2 * windowSize - 1);
    }
    
    // Если между первой страницей и началом окна есть пропуск
    if (start > 2) {
      pages.push('...');
    }
    
    // Добавляем страницы в окне (исключаем первую и последнюю)
    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    
    // Если между концом окна и последней страницей есть пропуск
    if (end < totalPages - 1) {
      pages.push('...');
    }
    
    // Всегда показываем последнюю страницу (если она не первая)
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold text-foreground flex items-center">
            Заявки
            {newTicketsCount > 0 && (
              <BellIcon className="h-5 w-5 ml-2 text-destructive" />
            )}
          </h1>
          {(newTicketsCount > 0 || unreadTicketsCount > 0) && (
            <div className="flex items-center space-x-2">
              {newTicketsCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-destructive/10 text-destructive border border-destructive/20">
                  {newTicketsCount} новых
                </span>
              )}
              {unreadTicketsCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-primary/10 text-primary border border-primary/20">
                  {unreadTicketsCount} непрочитанных
                </span>
              )}
            </div>
          )}
          {clientFilter && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-accent text-accent-foreground border border-border">
              Клиент: {clientFilter}
              <button
                onClick={() => {
                  setClientFilter('');
                  setSearchParams({});
                }}
                className="ml-1.5 hover:opacity-70"
              >
                ✕
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Открывать заявки в классической странице или в новом рабочем
              окне (бета). Выбор запоминается. */}
          <div className="hidden sm:flex items-center rounded-md border border-border overflow-hidden text-xs">
            <button
              onClick={() => changeViewMode('classic')}
              className={`px-2.5 py-1.5 transition-colors ${
                viewMode === 'classic' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
              }`}
              title="Открывать заявку на отдельной странице"
            >
              Классика
            </button>
            <button
              onClick={() => changeViewMode('workspace')}
              className={`px-2.5 py-1.5 transition-colors ${
                viewMode === 'workspace' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
              }`}
              title="Открывать заявку в рабочем окне со списком слева"
            >
              Окно (бета)
            </button>
          </div>
          <Link to="/tickets/new">
            <Button size="sm">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Создать
            </Button>
          </Link>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-card border border-border p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="search" className="block text-xs font-medium mb-1 text-foreground">
              Поиск
            </label>
            <div className="relative">
              <Input
                type="text"
                id="search"
                className="pl-8"
                placeholder="Поиск..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </div>

          <div className="min-w-[140px]">
            <label htmlFor="status" className="block text-xs font-medium mb-1 text-foreground">
              Статус
            </label>
            <select
              id="status"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Все</option>
              <option value="new">Новые</option>
              <option value="assigned">Назначенные</option>
              <option value="in_progress">В работе</option>
              <option value="resolved">Решенные</option>
              <option value="closed">Закрытые</option>
              <option value="paused">Пауза</option>
            </select>
          </div>

          <div className="min-w-[140px]">
            <label htmlFor="priority" className="block text-xs font-medium mb-1 text-foreground">
              Приоритет
            </label>
            <select
              id="priority"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">Все</option>
              <option value="low">Низкий</option>
              <option value="medium">Средний</option>
              <option value="high">Высокий</option>
              <option value="urgent">Срочный</option>
            </select>
          </div>

          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <div className="min-w-[160px]">
              <label htmlFor="csat" className="block text-xs font-medium mb-1 text-foreground">
                Оценка (CSAT)
              </label>
              <select
                id="csat"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={csatFilter}
                onChange={(e) => setCsatFilter(e.target.value)}
              >
                <option value="">Все</option>
                <option value="low">👎 Низкие (1–2★)</option>
                <option value="mid">😐 Нейтральные (3★)</option>
                <option value="high">👍 Высокие (4–5★)</option>
                <option value="1">1 ★</option>
                <option value="2">2 ★</option>
                <option value="3">3 ★</option>
                <option value="4">4 ★</option>
                <option value="5">5 ★</option>
                <option value="rated">С оценкой</option>
                <option value="unrated">Без оценки</option>
              </select>
            </div>
          )}

          <div className="min-w-[180px]">
            <label htmlFor="gameInstall" className="block text-xs font-medium mb-1 text-foreground">
              Установка/обновление игр
            </label>
            <select
              id="gameInstall"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={gameInstallFilter}
              onChange={(e) => setGameInstallFilter(e.target.value)}
            >
              <option value="">Все заявки</option>
              <option value="yes">🎮 Только с меткой</option>
              <option value="no">Скрыть с меткой</option>
            </select>
          </div>

          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setPriorityFilter('');
                setClientFilter('');
                setCsatFilter('');
                setGameInstallFilter('');
                setCurrentPage(1);
              }}
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              Сбросить
            </Button>
          </div>
        </div>
      </div>

      {/* Табличный список заявок */}
      <div className="bg-card border border-border overflow-hidden">
        <DataTable
          data={tickets}
          loading={loading}
          onRowClick={(ticket) => navigate(ticketHref(ticket.id))}
          getRowHref={(ticket) => ticketHref(ticket.id)}
          emptyMessage="Попробуйте изменить параметры поиска или создайте новую заявку."
          getRowClassName={(ticket) => {
            const isNew = isNewTicket(ticket);
            const isUnread = isUnreadTicket(ticket);
            if (isNew) {
              return 'bg-primary/5 border-l-2 border-l-primary';
            }
            if (isUnread) {
              return 'bg-accent/30 border-l-2 border-l-accent';
            }
            return '';
          }}
          columns={[
            {
              key: 'id',
              header: 'ID',
              className: 'px-3 py-2 whitespace-nowrap',
              cell: (ticket) => {
                const isNew = isNewTicket(ticket);
                const isUnread = isUnreadTicket(ticket);
                return (
                  <div className="flex items-center">
                    <span className="text-xs font-medium text-primary">
                      #{ticket.ticketNumber}
                    </span>
                    {isNew && (
                      <span className="ml-1.5 w-2 h-2 bg-destructive rounded-full" title="Новая заявка"></span>
                    )}
                    {isUnread && !isNew && (
                      <span className="ml-1.5 w-2 h-2 bg-primary rounded-full" title="Непрочитанная"></span>
                    )}
                  </div>
                );
              }
            },
            {
              key: 'subject',
              header: 'Тема',
              className: 'px-3 py-2',
              cell: (ticket) => (
                <div className="max-w-xs">
                  <div className="text-sm font-medium truncate text-foreground flex items-center gap-1.5">
                    {ticket.isGameInstall && (
                      <span title="Установка/обновление игр" className="shrink-0">🎮</span>
                    )}
                    <span className="truncate">{ticket.subject}</span>
                  </div>
                  {ticket.description && (
                    <div className="text-xs truncate mt-0.5 text-muted-foreground" title={ticket.description}>
                      {ticket.description}
                    </div>
                  )}
                </div>
              )
            },
            {
              key: 'client',
              header: 'Клиент',
              className: 'px-3 py-2 whitespace-nowrap hidden md:table-cell',
              cell: (ticket) => (
                <span className="text-xs text-muted-foreground">
                  {ticket.telegramChatType === 'private' 
                    ? (ticket.telegramUsername ? `@${ticket.telegramUsername}` : `${ticket.telegramFirstName || ''} ${ticket.telegramLastName || ''}`.trim() || 'Клиент')
                    : ticket.telegramChatTitle || 'Группа'
                  }
                </span>
              )
            },
            {
              key: 'status',
              header: 'Статус',
              className: 'px-3 py-2 whitespace-nowrap',
              cell: (ticket) => (
                <div className="flex items-center gap-1.5">
                  <StatusBadge status={ticket.status} />
                </div>
              )
            },
            {
              key: 'priority',
              header: 'Приоритет',
              className: 'px-3 py-2 whitespace-nowrap hidden lg:table-cell',
              cell: (ticket) => <PriorityBadge priority={ticket.priority} />
            },
            {
              key: 'assigned',
              header: 'Ответственный',
              className: 'px-3 py-2 whitespace-nowrap hidden lg:table-cell',
              cell: (ticket) => (
                ticket.assignedTo ? (
                  <span className="text-xs text-muted-foreground">
                    {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'date',
              header: 'Дата',
              className: 'px-3 py-2 whitespace-nowrap hidden sm:table-cell',
              cell: (ticket) => (
                <span className="text-xs text-muted-foreground">
                  {formatDateCompact(ticket.createdAt)}
                </span>
              )
            },
            {
              key: 'sla',
              header: 'SLA',
              className: 'px-3 py-2 whitespace-nowrap',
              cell: (ticket) => {
                const sla = calculateSLA(ticket);
                let slaStatus: "ok" | "warning" | "breach" = "ok";
                if (sla) {
                  if (sla.firstResponse.breached) {
                    slaStatus = "breach";
                  } else if (sla.firstResponse.remaining && sla.firstResponse.remaining < 60) {
                    slaStatus = "warning";
                  }
                }
                return sla ? (
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {sla.firstResponse.actual 
                        ? formatSLATime(sla.firstResponse.actual)
                        : `${formatSLATime(sla.firstResponse.remaining || 0)}`
                      }
                    </span>
                    <SLABadge status={slaStatus} />
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                );
              }
            },
            ...((user?.role === 'admin' || user?.role === 'superadmin') ? [{
              key: 'csat',
              header: 'CSAT',
              className: 'px-3 py-2 whitespace-nowrap',
              cell: (ticket: Ticket) => {
                const rating = (ticket as any).csat?.rating;
                const requested = (ticket as any).csat?.requestedAt;
                if (rating) {
                  const tone = rating <= 2
                    ? 'bg-rose-500/10 text-rose-600 border-rose-500/20'
                    : rating === 3
                      ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                      : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
                  return (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-xs font-semibold ${tone}`}>
                      {rating}<span className="text-[10px]">★</span>
                    </span>
                  );
                }
                return (
                  <span className="text-xs text-muted-foreground">
                    {requested ? 'ждём' : '—'}
                  </span>
                );
              },
            }] : []),
            {
              key: 'actions',
              header: 'Действия',
              className: 'px-3 py-2 whitespace-nowrap text-right',
              cell: (ticket) => (
                <div className="flex items-center justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
                  {(user?.role === 'admin' || user?.role === 'superadmin') && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDelete(ticket.id);
                      }}
                      title="Удалить заявку"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )
            }
          ]}
        />

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="px-3 py-2 flex items-center justify-between border-t border-border bg-card">
            {/* Мобильная версия: только навигация */}
            <div className="flex-1 flex items-center justify-between sm:hidden">
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                title="Первая страница"
              >
                <ChevronDoubleLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Назад
              </Button>
              <span className="text-xs font-medium px-2 text-foreground">
                {currentPage} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Вперед
              </Button>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Последняя страница"
              >
                <ChevronDoubleRightIcon className="h-4 w-4" />
              </Button>
            </div>

            {/* Десктопная версия: полная пагинация */}
            <div className="hidden sm:flex sm:items-center sm:justify-between sm:w-full">
              <div className="flex items-center space-x-1">
                <span className="text-xs text-muted-foreground">
                  Страница
                </span>
                <span className="text-xs font-medium text-foreground">
                  {currentPage}
                </span>
                <span className="text-xs text-muted-foreground">
                  из {totalPages}
                </span>
              </div>
              
              <nav className="flex items-center space-x-1">
                {/* Первая страница */}
                <Button
                  size="icon-sm"
                  variant="outline"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="Первая страница"
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                </Button>
                
                {/* Предыдущая страница */}
                <Button
                  size="icon-sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  title="Предыдущая страница"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                
                {/* Номера страниц */}
                <div className="flex items-center space-x-0">
                  {getPageNumbers(currentPage, totalPages).map((page, index) => {
                    if (page === '...') {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-2 py-1 text-xs text-muted-foreground"
                        >
                          ...
                        </span>
                      );
                    }
                    
                    const pageNum = page as number;
                    const isActive = pageNum === currentPage;
                    
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={isActive ? "default" : "outline"}
                        onClick={() => setCurrentPage(pageNum)}
                        className="min-w-[32px]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                {/* Следующая страница */}
                <Button
                  size="icon-sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  title="Следующая страница"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
                
                {/* Последняя страница */}
                <Button
                  size="icon-sm"
                  variant="outline"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Последняя страница"
                >
                  <ChevronDoubleRightIcon className="h-4 w-4" />
                </Button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketsPage;